<script lang="ts">
  import type { colourVariant } from "$lib/data/colours.data";
  import { createEventDispatcher } from "svelte";
  import { _ } from "svelte-i18n";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import SizeSwatch from "$lib/components/SizeSwatch/SizeSwatch/SizeSwatch.svelte";
  import ColorSwatch from "$lib/components/ColourSwatch/ColourSwatch/ColourSwatch.svelte";
  import DenominationSwatch from "$lib/components/DenominationSwatch/DenominationSwatch.svelte";
  import VariantSelection from "../VariantSelection/VariantSelection.svelte";
  import Badge from "$lib/components/Badge/Badge.svelte";
  import { ShopifyImage, Picture } from "@thisissoon/flitejs-frontend";
  import { ProductCardImageInputConfig } from "$lib/components/ProductList/ProductCard/ProductCard.image-config";
  import type {
    MoneyV2,
    Image,
    SelectedOptionInput as Option,
  } from "$lib/gql/gen/codegen";
  import Price from "$lib/components/Price/Price.svelte";
  import Link from "$lib/components/Link/Link.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import { ColourOptionType } from "$lib/data/colours.data";
  import ScrollGradient from "$lib/components/ScrollGradient/ScrollGradient.svelte";
  import type { SizeOption } from "$lib/data/size.data";
  import { processInlineLinks } from "$lib/utils/url";
  import { regionStore } from "$lib/stores/region";

  export let isGiftCard: boolean = false;
  export let pdp: boolean = false;
  export let showFindSize: boolean = false;
  export let title: string = "";
  export let subtitle: string = "";
  export let url: string = "";
  export let badge: string = "";
  export let cut: string = "";
  export let productImage: Image = undefined;
  export let productOptions: any[] = undefined;
  export let colourOptions: colourVariant[] = undefined;
  export let colourOptionType: ColourOptionType = ColourOptionType.SWATCH;
  export let waistSizeOptions: SizeOption[] = undefined;
  export let legSizeOptions: SizeOption[] | null = undefined;
  export let sizeOptions: SizeOption[] | null = undefined;
  export let denominationOptions: any | null = undefined;
  export let trouserSizeOptions: SizeOption[] | null = undefined;
  export let selectedColourOption: colourVariant | false = null;
  export let selectedWaistOption: any = null;
  export let selectedLegOption: any = null;
  export let selectedSizeOption: SizeOption = null;
  export let selectedDenominationOption: SizeOption = null;
  export let selectedOptions: Option[] = undefined;
  export let price: MoneyV2 = undefined;
  export let comparePrice: MoneyV2 = undefined;
  export let priceRange: {
    minVariantPrice: MoneyV2;
    maxVariantPrice: MoneyV2;
  } = undefined;
  export let comparePriceRange: {
    minVariantPrice: MoneyV2;
    maxVariantPrice: MoneyV2;
  } = undefined;
  export let hideViewDetails: boolean = false;
  export let trouserSizeError = null;

  $: selectedWaistOption = selectedWaistOption
    ? (selectedWaistOption = { value: selectedWaistOption })
    : { value: undefined };

  $: selectedLegOption = selectedLegOption
    ? (selectedLegOption = { value: selectedLegOption })
    : { value: undefined };

  $: selectedSizeOption = selectedSizeOption
    ? selectedSizeOption
    : { value: undefined };

  const dispatch = createEventDispatcher();

  let viewportWidth: number;
  let mdWidth = 768;
  let lgWidth = 1280;
  $: longBadge = badge?.length > 12;

  const regionInfo = regionStore();

  let region = $regionInfo && $regionInfo.locale && $regionInfo.region;
</script>

<style lang="scss">
  .option-container {
    padding: 16px;
  }

  .product-details {
    display: flex;
  }

  .image {
    width: calc(100% / 3);
  }

  .info {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: calc(100% / 3 * 2);
    padding-left: 1.5rem;
  }

  .title,
  .link {
    margin-bottom: 0.25rem;
  }

  .title--plp-w-badge {
    margin-top: 1rem;
  }

  .link {
    margin-top: 1rem;
    @include breakpoint(md) {
      margin-top: 2.5rem;
    }
  }

  .option-groups {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .option-group--margin {
    margin-bottom: 0.5rem;

    @include breakpoint(lg) {
      margin-bottom: 0;
    }
  }

  .option-group--denomination {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  .option-group--colour {
    position: relative;
    order: -1;
  }

  .heading {
    margin-bottom: 0.75rem;

    &--link {
      display: flex;
      gap: 1rem;
    }
  }

  hr {
    border: 0;
    height: 1px;
    background: $on-surface-10;
    margin: 1.5rem 0;
  }

  .actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    margin-top: 1rem;
    border-top: 1px solid $on-surface-10;
    width: 100%;
  }

  .option-container--pdp {
    display: flex;
    flex-direction: column;
    padding: 0;
    gap: 0px;

    .heading {
      display: flex;
      gap: 0.75rem;
    }

    .product-details {
      padding: 1.5rem 0 0;
    }

    .option-groups {
      padding-left: 0;
      padding-right: 0;
    }

    .option-group--colour :global(.colour-wrapper) {
      padding-right: 1rem;
    }

    .option-group--size,
    .option-group--waist,
    .option-group--leg {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;

      .heading {
        margin-top: 0.37rem;
      }
    }

    .info {
      width: 100%;
      flex-direction: column;
      flex-wrap: wrap;
      padding: 0;

      &__container {
        display: flex;
        justify-content: space-between;
        flex-direction: row;
      }

      .title {
        order: -1;
        width: calc(100% / 3 * 2);

        @include breakpoint(xl) {
          width: 50%;
        }

        &--pdp {
          align-items: center;
          display: flex;
          justify-content: space-between;
          width: 100%;
          @include breakpoint(lg) {
            display: flex;
            justify-content: space-between;
            width: 100%;
          }
        }
      }

      .badge {
        display: flex;
        justify-content: flex-end;

        &--long {
          width: 100%;
          margin-top: 0.75rem;
          @include breakpoint(lg) {
            margin-top: 1rem;
          }
        }
      }

      .subtitle {
        margin-top: 0.25rem;
      }

      // .price {
      //   margin-top: 1rem;
      //   @include breakpoint(lg) {
      //     margin-top: 1.5rem;
      //   }
      // }

      .price__gift-card {
        margin-bottom: 1rem;
      }
    }
  }

  .inline-anchor {
    display: flex;
    margin-right: auto;
    align-items: center;
    text-decoration: none;
    cursor: pointer;
    gap: 12px;

    &--pdp {
      padding-bottom: 24px;
      :global(.icon) {
        margin-right: 12px;
      }

      a {
        text-decoration: underline;
      }
    }
  }

  @include breakpoint(md) {
    .option-container {
      padding: 24px 44px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;

      hr {
        display: none;
      }
    }

    .option-container--pdp {
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      width: 100%;
      gap: 0px;

      .product-details,
      .option-groups {
        width: 100%;
      }

      .info {
        flex-direction: column;
        flex-wrap: wrap;
        width: 100%;
      }

      .option-group--colour {
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: 1px solid $on-surface-10;
      }
    }
  }

  @include breakpoint(lg) {
    .option-container {
      display: flex;
      flex-wrap: wrap;
      width: 100%;
      padding: 16px 16px 0 16px;
    }

    .option-container--pdp {
      padding: 0;
      gap: 0px;
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class={`option-container ${pdp ? "option-container--pdp" : ""}`}>
  {#if badge || title || subtitle || price || priceRange || comparePrice || comparePriceRange || !hideViewDetails}
    <div class="product-details">
      {#if productImage && productImage.url}
        <div class="image">
          <Picture
            imageInputConfig={ProductCardImageInputConfig}
            imageInput={new ShopifyImage(productImage.url)}
            scalingFactor={40}
            altText={productImage.altText}
          />
        </div>
      {/if}

      <div class="info">
        {#if pdp}
          {#if title && title !== null}
            <div class="title title--pdp">
              <Typography variant="h3-1" component="h1">{title}</Typography>
              {#if badge && !longBadge}
                <div class="badge">
                  <Badge label={badge} />
                </div>
              {/if}
            </div>
          {/if}

          <div class="info__container">
            <div>
              {#if subtitle && subtitle !== null}
                <div class="subtitle">
                  <Typography variant="body2" component="p">
                    {subtitle}
                  </Typography>
                </div>
              {/if}

              {#if badge && longBadge}
                <div class="badge--long">
                  <Badge label={badge} />
                </div>
              {/if}
            </div>

            {#if (price || priceRange || comparePrice || comparePriceRange) && !isGiftCard}
              <div class={isGiftCard ? "price__gift-card" : "price"}>
                <Price
                  {priceRange}
                  {comparePriceRange}
                  variantPrice={price}
                  variantComparePrice={comparePrice}
                />
              </div>
            {/if}
          </div>
        {:else}
          <div class="top-info-container">
            {#if badge}
              <div class="badge">
                <Badge label={badge} />
              </div>
            {/if}
            {#if title && title !== null}
              <div class={`title ${badge && "title--plp-w-badge"}`}>
                <Typography variant="h3-1" component="h1">{title}</Typography>
              </div>
            {/if}
            {#if (price || priceRange || comparePrice || comparePriceRange) && !isGiftCard}
              <div class="price">
                <Price
                  {priceRange}
                  {comparePriceRange}
                  variantPrice={price}
                  variantComparePrice={comparePrice}
                />
              </div>
            {/if}
          </div>
        {/if}

        {#if !pdp && !hideViewDetails}
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="link" on:click={() => dispatch("viewDetails")} on:keydown>
            <Link href={url} processedUrl text={$_("View details")} />
          </div>
        {/if}
      </div>
    </div>

    <hr />
  {/if}

  {#if productOptions && productOptions.length > 0}
    <div class="option-groups">
      {#each productOptions as option, i (option.name)}
        {#if option.name.toLowerCase() === "colour"}
          {#if colourOptions && colourOptions.length > 0}
            <div class="option-group--colour">
              <div class="heading">
                <Typography variant="overline" component="p"
                  >{$_("Colour")}{#if pdp}:{/if}</Typography
                >
                {#if pdp && selectedColourOption && selectedColourOption.identifier}
                  <Typography
                    variant="caption2"
                    component="span"
                    uppercase={false}
                    >{selectedColourOption.identifier}</Typography
                  >
                {/if}
              </div>
              <ScrollGradient
                gradientHeight={viewportWidth >= lgWidth
                  ? "108px"
                  : viewportWidth >= mdWidth
                    ? "98px"
                    : "83px"}
              >
                <div class="colour-container">
                  <ColorSwatch
                    swatchType={colourOptionType}
                    {colourOptions}
                    {selectedColourOption}
                    on:select={e => {
                      const detail = { ...e.detail, index: i };
                      selectedColourOption = e.detail;
                      dispatch("selectColourOption", detail);
                    }}
                  />
                </div>
              </ScrollGradient>
            </div>
          {/if}
        {:else if option.name.toLowerCase() === "size"}
          {#if trouserSizeOptions}
            {#if waistSizeOptions && waistSizeOptions.length > 0}
              <div class="option-group--waist">
                <div class="heading heading--link">
                  <Typography variant="overline" component="p"
                    >{$_("waist")}{#if pdp}:{/if}
                  </Typography>

                  {#if !pdp}
                    <!-- svelte-ignore a11y-missing-attribute -->
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <Typography variant="caption2" component="p" underline>
                      <!-- svelte-ignore a11y-no-static-element-interactions -->
                      <a
                        class=""
                        on:click|stopPropagation={() => {
                          dispatch("openSizeGuide");
                        }}>{$_("Size Guide")}</a
                      >
                    </Typography>
                  {/if}
                </div>

                <SizeSwatch
                  waist
                  sizeOptions={waistSizeOptions}
                  selectedSizeOption={selectedWaistOption}
                  on:select={e => {
                    selectedWaistOption = e.detail;
                    const detail = { ...e.detail, index: i };
                    dispatch("selectWaistSizeOption", detail);
                  }}
                />
              </div>
            {/if}

            {#if legSizeOptions && legSizeOptions.length > 0}
              <div
                class="option-group--leg {!colourOptions
                  ? 'option-group--margin'
                  : ''}"
              >
                <div class="heading">
                  <Typography variant="overline" component="p"
                    >{$_("legLength")}{#if pdp}:{/if}
                  </Typography>
                </div>

                <SizeSwatch
                  leg
                  sizeOptions={legSizeOptions}
                  selectedSizeOption={selectedLegOption}
                  on:select={e => {
                    selectedLegOption = e.detail;
                    const detail = { ...e.detail, index: i };
                    dispatch("selectLegSizeOption", detail);
                  }}
                />
              </div>
            {/if}
          {:else if sizeOptions}
            {#if sizeOptions && sizeOptions.length > 0}
              <div
                class="option-group--size {!colourOptions
                  ? 'option-group--margin'
                  : ''}"
              >
                <div class="heading heading--link">
                  <Typography variant="overline" component="p"
                    >{#if isGiftCard}{$_("denomination")}{:else}{$_(
                        "size",
                      )}{/if}{#if pdp}:{/if}
                  </Typography>

                  {#if !pdp && !isGiftCard}
                    <!-- svelte-ignore a11y-missing-attribute -->
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <Typography variant="caption2" component="p" underline>
                      <!-- svelte-ignore a11y-no-static-element-interactions -->
                      <a
                        class="inline-anchor"
                        on:click|stopPropagation={() => {
                          dispatch("openSizeGuide");
                        }}>{$_("Size Guide")}</a
                      >
                    </Typography>
                  {/if}
                </div>

                <SizeSwatch
                  {sizeOptions}
                  {selectedSizeOption}
                  on:select={e => {
                    selectedSizeOption = e.detail;
                    const detail = { ...e.detail, index: i };
                    dispatch("selectSizeOption", detail);
                  }}
                />
              </div>
            {/if}
          {/if}
        {:else if denominationOptions}
          {#if denominationOptions && denominationOptions.length > 0}
            <div class="option-group--denomination">
              <div class="heading heading--link">
                <Typography variant="overline" component="p"
                  >{$_("denomination")}:
                </Typography>
              </div>

              <DenominationSwatch
                {denominationOptions}
                {selectedDenominationOption}
                on:select={e => {
                  selectedDenominationOption = e.detail;
                  const detail = { ...e.detail, index: i };
                  dispatch("selectDenominationOption", detail);
                }}
              />
            </div>
          {/if}
        {:else}
          <VariantSelection
            selected={selectedOptions[i].value}
            on:change={e => {
              const detail = { ...e.detail, index: i };
              dispatch("selectVariant", detail);
            }}
            productOption={{
              id: option.id,
              ...option,
            }}
          />
        {/if}
      {/each}

      {#if trouserSizeError}
        <Typography variant="overline-small" component="span">
          {trouserSizeError}
        </Typography>
      {/if}
      {#if showFindSize}
        <div class="inline-anchor inline-anchor--pdp">
          <Icon icon="checkroom" color="on-surface" />
          <div>
            <Typography variant="body1">
              <span>
                {@html processInlineLinks($_("sizeGuide.message"))}
              </span>
            </Typography>
            <!-- svelte-ignore a11y-missing-attribute -->
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <Typography variant="body1">
              <a
                class=""
                on:click|stopPropagation={() => {
                  dispatch("openSizeGuide");
                }}
              >
                {@html processInlineLinks($_("sizeGuide.message.link"))}
              </a></Typography
            >
            <Typography variant="body1">
              {@html processInlineLinks($_("sizeGuide.message.end"))}
            </Typography>
          </div>
          {#if cut && cut !== ""}
            <div class="badge">
              <Badge label={cut} />
            </div>
          {/if}
        </div>
      {/if}
    </div>
  {/if}
</div>
